<template>
    <GeneralDialogTemplate @close="$emit('close')">
        <template slot="header">
            <span class="font-weight-medium">{{ $t("Edit") }} - {{ $t("Calendar") }}</span>
        </template>
        <form>

            <v-row no-gutters>
                <v-col class="col-6 pa-2">
                    <v-select
                        v-model="form_data.type"
                        :label="$t('Event type') + ' *'"
                        :items="types"
                        item-text="name"
                        item-value="code"
                        class="mt-3"
                        outlined
                        :menu-props="{ offsetY: true }"
                        :error-messages="form_data.type_error"
                        @change="setDefaultDuration"
                    ></v-select>
                </v-col>

                <v-col class="col-6 pa-2">
                    <v-select
                        v-if="is_admin"
                        v-model="form_data.country_code"
                        class="mt-3"
                        :label="$t('Country')"
                        :items="countries"
                        item-text="name"
                        item-value="code"
                        outlined
                        :menu-props="{ offsetY: true }"
                        :error-messages="form_data.country_code_error"
                        @change="setDefaultDuration"
                    ></v-select>
                </v-col>
            </v-row>

            <v-row no-gutters>
                <v-col class="col-6 pa-2">
                    <v-menu ref="menu" :close-on-content-click="false" offset-y min-width="280" nudge-top="19">
                        <template v-slot:activator="{ on }">
                            <v-text-field
                                :value="form_data.date_from | format_date"
                                :label="$t('Date from') + ' *'"
                                :error-messages="form_data.date_from_error"
                                outlined
                                append-icon="event"
                                readonly
                                color="primary"
                                v-on="on"
                            />
                        </template>
                        <v-card class="date-range pb-3">
                            <v-date-picker
                                ref="picker"
                                v-model="form_data.date_from"
                                :locale="formatting.culture"
                                min="1900-01-01"
                                :first-day-of-week="formatting.first_week_day"
                                no-title
                                scrollable
                                class="elevation-0"
                            />
                        </v-card>
                    </v-menu>
                </v-col>
                <v-col class="col-6 pa-2">
                    <v-menu ref="menu" :close-on-content-click="false" offset-y min-width="280" nudge-top="19">
                        <template v-slot:activator="{ on }">
                            <v-text-field
                                :value="form_data.date_until | format_date"
                                :label="$t('Date until')"
                                :error-messages="form_data.date_until_error"
                                outlined
                                append-icon="event"
                                readonly
                                color="primary"
                                v-on="on"
                            />
                        </template>
                        <v-card class="date-range pb-3">
                            <v-date-picker
                                ref="picker"
                                v-model="form_data.date_until"
                                :locale="formatting.culture"
                                min="1900-01-01"
                                :first-day-of-week="formatting.first_week_day"
                                no-title
                                scrollable
                                class="elevation-0"
                            />
                        </v-card>
                    </v-menu>
                </v-col>
                <v-col
                    v-if="form_data.type === 'shortened_working_day'"
                    class="col-6 pa-2"
                >
                    <text-field
                        :value="formattedDuration"
                        :label="$t('Shortened by h')"
                        @input="formattedDuration = $event"
                        @blur="setFormattedDuration"
                    />
                </v-col>
            </v-row>

            <v-row no-gutters>
                <v-col class="col-12 pa-2">
                    <v-text-field
                        v-model="form_data.name"
                        :label="$t('Name') + ' *'"
                        outlined
                        :hide-details="!form_data.name_error"
                        :error-messages="form_data.name_error"
                    ></v-text-field>
                </v-col>
            </v-row>

            <v-row no-gutters>
                <v-col class="col-12 pa-2">
                    <v-text-field
                        v-model="form_data.link"
                        :label="$t('Link')"
                        outlined
                        :hide-details="!form_data.link_error"
                        :error-messages="form_data.link_error"
                    ></v-text-field>
                </v-col>
            </v-row>

            <v-row no-gutters>
                <v-col class="col-12 pa-2">
                    <v-select
                        v-if="is_admin"
                        v-model="form_data.area_id"
                        :label="$t('Category')"
                        :items="business_areas"
                        item-text="name_key"
                        item-value="id"
                        outlined
                        :menu-props="{ offsetY: true }"
                        :error-messages="form_data.area_id_error"
                    ></v-select>
                </v-col>
            </v-row>

        </form>
        <template slot="footer">
            <v-spacer/>
            <v-btn color="primary" text @click="$emit('close')">
                {{ $t("Cancel") }}
            </v-btn>
            <v-btn color="primary inverted--text" :loading="loading" class="px-5" @click="save()">
                {{ $t("Save") }}
            </v-btn>
        </template>
    </GeneralDialogTemplate>
</template>

<script>
import Vue from "vue";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import {computed, ref} from "@vue/composition-api";
import GeneralDialogTemplate from "../Dialog/GeneralDialogTemplate";
import AppModule from "../../store/modules/app";
import CalendarEventModule from "../../store/modules/calendar_event";
import AccountModule from "../../store/modules/account";
import {formatTimeDuration, parseSysEndDate, parseTimeDuration} from "@/plugins/formatting";

export default {
    name: "EditAdminCalendarDialog",
    components: {
        GeneralDialogTemplate
    },
    props: {
        eventId: {
            type: Number,
            required: true
        }
    },
    setup(props, context) {
        const is_admin = true;
        const loading = false;
        const form_data = ref(null);
        const classifiers_loaded = ref(false);
        const formattedDuration = ref("")

        const updateFormattedDuration = async () => {
            formattedDuration.value = formatTimeDuration(form_data.value.duration);
        }

        const setFormattedDuration = (event) => {
            let value = event.target.value
            value = value.trim()

            form_data.value.duration = parseTimeDuration(value);
            updateFormattedDuration()
        }

        const setDefaultDuration = () => {
            if (form_data.value.type !== 'shortened_working_day') {
                form_data.value.duration = 0
                updateFormattedDuration()
                return
            }

            let duration = 60 * 60 * 3

            if (form_data.value.country_code === 'lv' || form_data.value.country_code === 'lt') {
                duration = 60 * 60
            }

            form_data.value.duration = duration
            updateFormattedDuration()
        }

        const appModule = AppModule();
        const {formatting} = AppModule();
        const {current_account} = AccountModule();

        const calendarEventModule = CalendarEventModule();
        const types = calendarEventModule.types.value;

        const countries = computed(function () {
            return appModule.countries.value;
        });
        const business_areas = computed(function () {
            return appModule.business_areas.value;
        });

        const load_data = function () {
            form_data.value = Vue.prepare_form_data({
                date_from: null,
                date_until: null,
                name: null,
                type: null,
                area_id: null,
                country_code: null,
                link: null,

                date_from_error: null,
                date_until_error: null,
                area_id_error: null,
                name_error: null,
                type_error: null,
                country_code_error: null,
                link_error: null,
            });
            appModule
                .fetchClassifiers({types: ["countries", "business_areas"], account_id: current_account.value.id})
                .then(function () {
                    classifiers_loaded.value = true;
                })
                .catch(function (ex) {
                    errorDialog.showError(ex);
                });

            if (props.eventId > 0) {
                calendarEventModule.fetchAdminCalendarEvent(props.eventId)
                    .then(function (item) {
                        let date_from = item.date_from.split(" ")
                        date_from = (typeof date_from !== "undefined") ? date_from[0] : ""

                        let date_until = item.date_until.split(" ")
                        date_until = (typeof date_until !== "undefined") ? date_until[0] : ""

                        form_data.value = Vue.prepare_form_data({
                            date_from: date_from,
                            date_until: parseSysEndDate(date_until),
                            name: item.name,
                            type: item.type,
                            area_id: item.area_id,
                            country_code: item.country_code ? item.country_code.toLowerCase() : "",
                            link: item.link,
                            duration: item.duration ? item.duration : 0,
                            name_error: null,
                            type_error: null,
                            date_from_error: null,
                            date_until_error: null,
                            area_id_error: null,
                            country_code_error: null,
                            link_error: null,
                        });

                        updateFormattedDuration()
                    })
                    .catch(function (ex) {
                        appModule.handleErrorResponse(form_data.value, ex);
                    });
            }
        };

        const validate = function () {
            return true;
        };
        const save = function () {
            appModule.clearValidationErrors(form_data.value);
            if (!validate()) return;

            const data = Vue.clean_and_clone_object(form_data.value);
            const event_id = props.eventId ? props.eventId : 0;
            calendarEventModule.saveAdminCalendarEvent(event_id, data)
                .then(function () {
                    context.emit("close", data);
                })
                .catch(function (ex) {
                    appModule.handleErrorResponse(form_data.value, ex);
                });
        };

        load_data();

        return {
            form_data,
            loading,
            save,
            countries,
            business_areas,
            types,
            formatting,
            is_admin,
            setFormattedDuration,
            formattedDuration,
            setDefaultDuration
        };
    }
};
</script>
